'use client';

import {useEffect, useState } from 'react';
import Image from 'next/image';
import { FadeIn, FAQSection, HeroSection } from '../../../components';
import { ContactForm } from '../../../components/forms/ContactForm';
import CalcomWidget from '../../../components/booking/CalcomWidget';
import content from '../../../content/pages/kontakt.json';
import ModelCard from '../../../components/3d/ModelCard';
import ModelDetailModal from '../../../components/3d/ModelDetailModal';
import modelsData from '../../../content/3d-models.json';
import SebastianBust from '../../../components/3d/SebastianBust';

  
export default function KontaktPage() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const handleError = () => {
    setShowError(true);
    setTimeout(() => setShowError(false), 5000);
  };

  return (
    <>
    <HeroSection
          headline={content.hero.headline}
          subheadline={content.hero.subheadline}
          cta={content.hero.cta}
          logo={{
            light: '/images/general/logo-black.png',
            dark: '/images/general/logo-white.png',
            alt: 'Codeschnipsel Logo',
            width: 800,
            height: 240,
          }}
          backgroundImage={content.hero.backgroundImage}
          backgroundImageAlt="Kontakt aufnehmen für Webentwicklung und digitale Lösungen"
          fullHeight={true}
          overlay={true}
          overlayOpacity={0.6}
          alignment="center"
      />

      {/* Intro Section */}
      <section className="py-20 md:py-32 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12 space-y-6">
                {content.intro.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-accent">$1</strong>'),
                    }}
                  />
                ))}
              </div>


              {/* Availability Badge */}
              <div className="bg-accent/10 dark:bg-accent/20 border border-accent/20 dark:border-accent/30 rounded-lg p-6 text-center">
                <div className="text-sm font-semibold text-accent uppercase tracking-wider mb-2">
                  {content.intro.availability.status}
                </div>
                <div className="text-2xl font-semibold text-primary dark:text-gray-100 mb-2">
                  {content.intro.availability.date}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {content.intro.availability.note}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 md:py-32 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-4xl mx-auto mb-12 text-center">
              <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl text-gray-900 dark:text-gray-100">
                {content.contact.headline}
              </h2>
            </div>
          </FadeIn>

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {content.contact?.methods?.map((method, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <div
                  className={`bg-white dark:bg-gray-800 rounded-lg p-8 h-full ${
                    method.highlight
                      ? 'border-2 border-accent shadow-lg'
                      : 'border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-accent/10 dark:bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl">
                        {method.icon === 'calendar' && '📅'}
                        {method.icon === 'mail' && '✉️'}
                        {method.icon === 'phone' && '📞'}
                        {method.icon === 'message-circle' && '💬'}
                      </span>
                    </div>
                    <div className="flex-grow">
                      <div className="font-heading font-semibold text-xl mb-2 text-gray-900 dark:text-gray-100">
                        {method.title}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                        {method.description}
                      </p>

                      {method.value && (
                        <p className="font-mono text-accent font-semibold mb-2">
                          {method.value}
                        </p>
                      )}

                      {method.note && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-4">
                          {method.note}
                        </p>
                      )}

                      {method.cta && (
                        <a
                          href={method.cta.href}
                          className={`inline-block px-6 py-3 rounded-lg font-semibold text-gray-900 btn transition-colors ${
                            method.highlight
                              ? 'bg-accent hover:bg-accent/90 btn text-gray-900'
                              : 'bg-accent hover:bg-gray-200 text-gray-900'
                          }`}
                        >
                          {method.cta.label}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

         {/* Cal.com Booking Section */}
      <section id="calendar" className="py-20 md:py-32 bg-gray-50 dark:bg-dark-2">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <p className="text-accent font-semibold uppercase tracking-wider text-sm mb-2">
                  {content.calendar.subheadline}
                </p>
                <h2 className="font-heading font-semibold text-3xl md:text-4xl mb-4 text-gray-900 dark:text-gray-100">
                  {content.calendar.headline}
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-8">
                  {content.calendar.intro}
                </p>
              </div>

              {/* Event Types Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {content.calendar?.eventTypes?.map((event, index) => (
                  <FadeIn key={event.id} delay={index * 0.1}>
                    <div
                      className={`bg-white dark:bg-dark-1 rounded-xl p-6 border-2 transition-all duration-300 hover:shadow-lg ${
                        event.recommended
                          ? 'border-accent shadow-md'
                          : event.urgent
                          ? 'border-red-500/30 bg-red-50 dark:bg-red-900/10'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            event.urgent
                              ? 'bg-red-500/10'
                              : 'bg-accent/10'
                          }`}>
                            <span className="text-2xl">
                              {event.icon === 'coffee' && '☕'}
                              {event.icon === 'lightbulb' && '💡'}
                              {event.icon === 'users' && '👥'}
                              {event.icon === 'alert-circle' && '🚨'}
                            </span>
                          </div>
                          <div>
                            <div className="font-heading font-semibold text-xl mb-1 text-gray-900 dark:text-gray-100">
                              {event.title}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {event.subtitle}
                            </p>
                          </div>
                        </div>
                        {event.recommended && (
                          <span className="bg-accent text-white text-xs font-semibold px-2 py-1 rounded">
                            Empfohlen
                          </span>
                        )}
                      </div>

                      {/* Details */}
                      <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">
                        {event.description}
                      </p>

                      {/* Meta Info */}
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <div className="text-gray-500 dark:text-gray-400">Dauer</div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{event.duration}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 dark:text-gray-400">Preis</div>
                          <div className={`font-semibold ${
                            event.price === 'Kostenlos'
                              ? 'text-green-600 dark:text-green-400'
                              : event.price === 'Kostenpflichtig'
                              ? 'text-red-600 dark:text-red-400'
                              : ''
                          }`}>
                            {event.price}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-gray-500 dark:text-gray-400">Verfügbarkeit</div>
                          <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{event.availability}</div>
                        </div>
                      </div>

                      {/* CTA Button */}
                      <CalcomWidget calLink={event.calLink} namespace={event.id} />
                    </div>
                  </FadeIn>
                ))}
              </div>

              {/* Note */}
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center italic">
                {content.calendar.note}
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 md:py-32 bg-white dark:bg-dark-2">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading font-semibold text-3xl md:text-4xl lg:text-5xl mb-4 dark:text-white">
                  {content.form.headline}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {content.form.intro}
                </p>
              </div>

              <ContactForm
                fields={content.form.fields}
                privacy={content.form.privacy}
                button={content.form.button}
                onSuccess={handleSuccess}
                onError={handleError}
              />

              {/* Success Message */}
              {showSuccess && (
                <div className="mt-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg">
                  <p className="font-semibold">✅ {content.form.success.headline}</p>
                  <p className="text-sm mt-1">{content.form.success.message}</p>
                </div>
              )}

              {/* Error Message */}
              {showError && (
                <div className="mt-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg">
                  <p className="font-semibold">❌ {content.form.error.headline}</p>
                  <p className="text-sm mt-1">{content.form.error.message}</p>
                </div>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Address & Contact Info */}
      <section className="py-20 md:py-32 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading font-semibold text-3xl md:text-4xl mb-12 text-center text-gray-900 dark:text-gray-100">
                {content.address.headline}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Address */}
                <div>
                  <div className="font-semibold text-xl mb-4 text-gray-900 dark:text-gray-100">Adresse</div>
                  <div className="space-y-2 text-gray-700 dark:text-gray-300">
                    <p className="font-semibold">{content.address.name}</p>
                    <p>{content.address.company}</p>
                    <p>{content.address.street}</p>
                    <p>{content.address.city}</p>
                    <p>{content.address.country}</p>
                  </div>

                  <div className="font-semibold text-xl mt-8 mb-4 text-gray-900 dark:text-gray-100">Kontakt</div>
                  <div className="space-y-2 text-gray-700 dark:text-gray-300">
                    <p>
                      <a
                        href={`mailto:${content.address.email}`}
                        className="text-accent hover:underline"
                      >
                        {content.address.email}
                      </a>
                    </p>
                    <p>{content.address.phone}</p>
                  </div>
                </div>

                {/* Availability & Social */}
                <div>
                  <div className="font-semibold text-xl mb-4 text-gray-900 dark:text-gray-100">Erreichbarkeit</div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line mb-8">
                    {content.address.availability}
                  </p>

                  <div className="font-semibold text-xl mb-4 text-gray-900 dark:text-gray-100">Social Media</div>
                  <div className="flex gap-4">
                    {content.address?.social?.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-gray-900 dark:bg-gray-700 hover:bg-accent/20 dark:hover:bg-accent/30 rounded-lg flex items-center justify-center transition-colors"
                        title={social.platform}
                      >
                        <span className="text-accent font-semibold">
                          {social.platform.charAt(0)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
  

      {/* FAQ */}
      <FAQSection
        headline={content.faq.headline}
        items={content.faq.items}
        className="bg-gray-50"
      />

      {/* CTA */}
      <section className="py-20 md:py-32 bg-primary text-white dark:text-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading font-semibold text-3xl md:text-4xl mb-4">
                {content.cta.headline}
              </h2>
              <p className="text-xl mb-8">
                {content.cta.description}
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                {content.cta?.buttons?.map((btn, index) => (
                  <a
                    key={index}
                    href={btn.href}
                    className={`px-8 py-4 btn rounded-lg font-semibold transition-colors ${
                      btn.primary
                        ? 'bg-gray-900 dark:bg-gray-900 hover:bg-accent/90 text-white'
                        : 'bg-white hover:bg-gray-900 text-gray-700 hover:text-white'
                    }`}
                  >
                    {btn.label}
                  </a>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(content.structuredData),
        }}
      />
    </>
  );
}
